import {
  ComponentType,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { SvgIconProps } from '@mui/material'
import { Palette, PaletteColor } from '@mui/material/styles'

/**
 * Theme palette colors an entry's card icon can use — any palette key backed by
 * a full {@link PaletteColor}, so `theme.palette[color].main` resolves. Derived
 * from the palette so it stays in sync with the theme instead of duplicating it.
 * Defaults to `primary`.
 */
export type RelevantIDColor = {
  [K in keyof Palette]: Palette[K] extends PaletteColor ? K : never
}[keyof Palette]

export type RelevantIDEntry = {
  /** Section heading this entry is clustered under in the dialog. */
  group: string
  /** Primary, human-readable name for the entry (e.g. the project's name). */
  label: string
  /** Kind of thing this ID belongs to (e.g. 'Project', 'Document'). */
  type?: string
  /** The ID to copy, and the key entries are de-duplicated by. */
  id: string
  /** Supporting text shown under the label, for telling similar entries apart. */
  secondary?: string
  /**
   * Icon shown on the entry's card. Pass the icon component itself (e.g.
   * `Description`), not an element — a stable reference keeps
   * {@link useRegisterRelevantID}'s effect from re-running every render.
   * Falls back to a generic icon when omitted.
   */
  icon?: ComponentType<SvgIconProps>
  /** Theme palette color for the card's icon tile. Defaults to `primary`. */
  color?: RelevantIDColor
}

type RelevantIDsContextValue = {
  entries: RelevantIDEntry[]
  registerRelevantID: (entry: RelevantIDEntry) => void
  unregisterRelevantID: (id: string) => void
}

// No-op defaults so components that register IDs (e.g. ProjectProvider,
// FieldTable) can render outside a RelevantIDsProvider — in isolated unit
// tests, or anywhere the dialog isn't mounted — without crashing.
const RelevantIDsContext = createContext<RelevantIDsContextValue>({
  entries: [],
  registerRelevantID: () => {},
  unregisterRelevantID: () => {},
})

export const useRelevantIDsContext = () => useContext(RelevantIDsContext)

type RelevantIDsProviderProps = {
  children: ReactNode
}

export default function RelevantIDsProvider({
  children,
}: RelevantIDsProviderProps) {
  // Ref-counted so the same ID registered by more than one mounted component
  // survives until every registrant has unmounted, instead of disappearing
  // (or duplicating) based on unmount order.
  const registryRef = useRef(
    new Map<string, { entry: RelevantIDEntry; refCount: number }>(),
  )
  const [entries, setEntries] = useState<RelevantIDEntry[]>([])

  const syncEntries = useCallback(() => {
    setEntries(Array.from(registryRef.current.values(), ({ entry }) => entry))
  }, [])

  const registerRelevantID = useCallback(
    (entry: RelevantIDEntry) => {
      const existing = registryRef.current.get(entry.id)
      if (existing) {
        existing.entry = entry
        existing.refCount += 1
      } else {
        registryRef.current.set(entry.id, { entry, refCount: 1 })
      }
      syncEntries()
    },
    [syncEntries],
  )

  const unregisterRelevantID = useCallback(
    (id: string) => {
      const existing = registryRef.current.get(id)
      if (!existing) return
      existing.refCount -= 1
      if (existing.refCount <= 0) {
        registryRef.current.delete(id)
      }
      syncEntries()
    },
    [syncEntries],
  )

  const value = useMemo(
    () => ({ entries, registerRelevantID, unregisterRelevantID }),
    [entries, registerRelevantID, unregisterRelevantID],
  )

  return (
    <RelevantIDsContext.Provider value={value}>
      {children}
    </RelevantIDsContext.Provider>
  )
}
